import { SupplyPosition } from '../../services/supplyService';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { Material } from '../../sp-types/materialType';
import { MonthYear } from '../../utils/MonthYear/monthYear';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { toNumber } from '../../utils/positionUtils';

/**
 * Transform Postgres supply positions and inventory reports into Dashboard stacked bar chart format
 * Matches the format used by demandAdapter and hedgesAdapter
 */

type SupplyByFormula = Record<string, { quantity: number; value: number }[]>;

const getMonthKey = (date: MonthYear): string => `${date.year}-${date.month}`;

/**
 * Aggregate supply positions by formula and month
 * Special handling: on-hand inventory is aggregated separately with 'on-hand' key
 */
const aggregateSupplyByMonth = (
  supplies: SupplyPosition[],
  inventoryReports: IInventoryReportType[],
  getRateToBase: (currency: string) => number | null,
  baseCurrency: string,
  pricesMap: Record<string, number>,
): Record<string, Record<string, { quantity: number; value: number; marketValue: number; hasNonPricedContracts?: boolean }>> => {
  const result: Record<string, Record<string, { quantity: number; value: number; marketValue: number; hasNonPricedContracts?: boolean }>> = {};

  // Filter out ratio POs (no fixed price, excluded from calcs)
  const filteredSupplies = supplies.filter(s => s.supply_type !== 'ratio_po');

  // Process supply positions
  filteredSupplies.forEach((supply) => {
    const formulaCode = supply.formula_code;
    
    // Special handling for on-hand inventory: use 'on-hand' as key instead of month
    const key = supply.supply_type === 'on_hand_inventory' 
      ? 'on-hand'
      : (() => {
          const [year, month] = supply.delivery_or_available_period.split('-').map(Number);
          const monthYear: MonthYear = { year, month };
          return getMonthKey(monthYear);
        })();

    if (!result[formulaCode]) {
      result[formulaCode] = {};
    }
    if (!result[formulaCode][key]) {
      result[formulaCode][key] = { quantity: 0, value: 0, marketValue: 0, hasNonPricedContracts: false };
    }

    // Use total_quantity for Dashboard (original quantity before API transformation)
    // Note: API now transforms quantity to be remaining_quantity, but Dashboard needs total
    const quantity = toNumber(supply.total_quantity);
    
    // Check if contract has a price
    const hasContractPrice = supply.unit_price_amount !== null && supply.unit_price_amount !== undefined;
    const contractPrice = hasContractPrice ? toNumber(supply.unit_price_amount) : 0;
    const unitPriceCurrency = supply.unit_price_currency || baseCurrency;

    // Use spot price as fallback when contract price is not available
    let unitPriceInBase = contractPrice;
    
    if (!hasContractPrice || contractPrice === 0) {
      // Fallback to spot price
      const spotPrice = pricesMap[formulaCode] || 0;
      unitPriceInBase = spotPrice;
      
      // Track that we used spot price fallback (for UI notification)
      if (quantity > 0 && spotPrice > 0) {
        result[formulaCode][key].hasNonPricedContracts = true;
      }
    } else {
      // Convert contract price to base currency
      if (unitPriceCurrency.toUpperCase() !== baseCurrency.toUpperCase()) {
        const rate = getRateToBase(unitPriceCurrency);
        if (rate !== null && rate !== 0) {
          // Rates are stored as: 1 BASE = rate * CURRENCY
          // To convert CURRENCY → BASE we divide: amount / rate
          unitPriceInBase = contractPrice / rate;
        }
      }
    }

    // Calculate market value using spot price (for chart card display)
    const spotPrice = pricesMap[formulaCode] || 0;
    
    result[formulaCode][key].quantity += quantity;
    result[formulaCode][key].value += quantity * unitPriceInBase;  // Contract/actual price
    result[formulaCode][key].marketValue += quantity * spotPrice;  // Market/spot price
  });

  // Process inventory reports
  inventoryReports.forEach((report) => {
    const formulaCode = report.formulaId;
    const monthYear: MonthYear = report.monthYear;
    const key = getMonthKey(monthYear);

    if (!result[formulaCode]) {
      result[formulaCode] = {};
    }
    if (!result[formulaCode][key]) {
      result[formulaCode][key] = { quantity: 0, value: 0, marketValue: 0 };
    }

    const quantity = toNumber(report.quantity);
    const price = toNumber(report.actualPrice);

    result[formulaCode][key].quantity += quantity;
    result[formulaCode][key].value += quantity * price;  // Actual price from inventory reports
    result[formulaCode][key].marketValue += quantity * price;  // Same as actual for inventory reports
  });

  return result;
};

/**
 * Fill in months based on exposure horizon - returns separate arrays for quantity and monetary
 * Special handling: if 'on-hand' key exists, it's added as the first entry
 */
const fillMonthsArrays = (
  monthlyValues: Record<string, { quantity: number; value: number; marketValue: number }>,
  horizonMonths: number = 12,
): { quantityArray: { date: MonthYear; value: number }[]; monetaryArray: { date: MonthYear; value: number }[]; marketMonetaryArray: { date: MonthYear; value: number }[] } => {
  const now = new Date();
  const quantityArray: { date: MonthYear; value: number }[] = [];
  const monetaryArray: { date: MonthYear; value: number }[] = [];
  const marketMonetaryArray: { date: MonthYear; value: number }[] = [];

  // Check if we have on-hand inventory
  const onHandData = monthlyValues['on-hand'];
  if (onHandData) {
    // Add on-hand as first entry with special month value (0, 0) to identify it
    quantityArray.push({
      date: { month: 0, year: 0 }, // Special marker for on-hand
      value: onHandData.quantity,
    });
    
    monetaryArray.push({
      date: { month: 0, year: 0 }, // Special marker for on-hand
      value: onHandData.value,  // Contract/actual price
    });
    
    marketMonetaryArray.push({
      date: { month: 0, year: 0 }, // Special marker for on-hand
      value: onHandData.marketValue,  // Market/spot price
    });
  }

  for (let i = 0; i < horizonMonths; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthYear: MonthYear = {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
    const key = getMonthKey(monthYear);
    const monthData = monthlyValues[key];
    
    quantityArray.push({
      date: monthYear,
      value: monthData ? monthData.quantity : 0,
    });
    
    monetaryArray.push({
      date: monthYear,
      value: monthData ? monthData.value : 0,  // Contract/actual price
    });
    
    marketMonetaryArray.push({
      date: monthYear,
      value: monthData ? monthData.marketValue : 0,  // Market/spot price
    });
  }

  return { quantityArray, monetaryArray, marketMonetaryArray };
};

/**
 * Main adapter function: Transform supply data to stacked bar chart elements
 */
export const transformSupplyToStackedBar = (
  supplies: SupplyPosition[] | undefined,
  inventoryReports: IInventoryReportType[] | undefined,
  selectedFormulaId: string,
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  pricesMap: Record<string, number>,
  isQuantity: boolean,
  horizonMonths: number = 12,
  getRateToBase?: (currency: string) => number | null,
  baseCurrency: string = 'USD',
): StackedBarChartElement[] => {
  if ((!supplies || supplies.length === 0) && (!inventoryReports || inventoryReports.length === 0)) {
    return [];
  }

  // Default getRateToBase if not provided (no conversion)
  const defaultGetRateToBase = (currency: string) => currency.toUpperCase() === baseCurrency.toUpperCase() ? 1 : null;
  const rateConverter = getRateToBase || defaultGetRateToBase;

  const aggregated = aggregateSupplyByMonth(supplies || [], inventoryReports || [], rateConverter, baseCurrency, pricesMap);
  const isGeneralMode = selectedFormulaId === GENERAL_ID;
  const formulasToShow = isGeneralMode
    ? Object.keys(aggregated)
    : Object.keys(aggregated).filter(f => f === selectedFormulaId);

  const result: StackedBarChartElement[] = formulasToShow.map((formulaCode) => {
    const monthlyValues = aggregated[formulaCode] || {};
    const { quantityArray, monetaryArray, marketMonetaryArray } = fillMonthsArrays(monthlyValues, horizonMonths);
    
    // Calculate sums
    const sumQuantity = quantityArray.reduce((sum, item) => sum + toNumber(item.value), 0);
    const sumMonetary = monetaryArray.reduce((sum, item) => sum + toNumber(item.value), 0);
    const sumMarketMonetary = marketMonetaryArray.reduce((sum, item) => sum + toNumber(item.value), 0);

    return {
      formulaId: formulaCode,
      fieldName: materialsMap[formulaCode]?.name || formulaCode,
      unit: materialsMap[formulaCode]?.unit,
      price: pricesMap[formulaCode] || 0,
      color: colorsMap[formulaCode] || '#6b7280',
      valueArray: isQuantity ? quantityArray : marketMonetaryArray, // Use market prices for chart card
      quantityArray,
      monetaryArray,  // Keep for modal dialog toggle
      marketMonetaryArray,  // New: market/spot prices
      sumValue: isQuantity ? sumQuantity : sumMarketMonetary, // Use market prices for chart card
      sumQuantity,
      sumMonetary,  // Keep for modal dialog toggle
      sumMarketMonetary,  // New: market/spot price sum
      totalWeight: sumQuantity,
    };
  });

  return result;
};
